import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { SnCamerasAPI, SnIncidentsAPI } from '@lib/api/services';
import { ChannelsToShareDialogComponent } from '@lib/communication/twiliochat/components';
import { SnChannel } from '@lib/communication/twiliochat/models';
import { SnMessagesService } from '@lib/communication/twiliochat/services';
import { t } from '@lib/translation';
import { SnShareImageDialogComponent } from '@lib/ui/image-editor';
import { SentryVaultComponent } from '@lib/vault';

import { ClipViewVideoDialogComponent } from '../components/clip-view-video-dialog/clip-view-video-dialog.component';
import { DetectionDetailsDialogComponent } from '../components/detection-details-dialog/detection-details-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class SentryActionsService {
  public reporting: boolean;
  public reported: boolean;

  public toastContainer: ToastContainerDirective;

  private readonly _destroy$ = new Subject<void>();

  constructor(
    private readonly _dialog: MatDialog,
    private readonly _incidentsAPI: SnIncidentsAPI,
    private readonly _messages: SnMessagesService,
    private readonly _toastrService: ToastrService,
    private readonly _camerasAPI: SnCamerasAPI,
  ) {}

  public setViewClip(item: any, data: any): void {
    this._camerasAPI
      .getCamera(item.cameraId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: (camera) => {
          this._dialog.open(ClipViewVideoDialogComponent, {
            data: {
              camera,
              item,
              snapshot: item.detectionsImageUrl,
              resolution: item.resolution,
              aiBox: data.box,
            },
          });
        },
        error: () => {
          this._toastrService.error(t('map.clipViewVideo.clipNotAvailable'));
        },
      });
  }

  public sendToVault(url: string): void {
    this._dialog
      .open(SentryVaultComponent)
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((vaultId) => {
        const vault = vaultId.id || vaultId;
        if (vault) {
          this.addVaultImage(vault, url);
        }
      });
  }

  public getReporting(): boolean {
    return this.reporting;
  }

  public getReported(): boolean {
    return this.reported;
  }

  public addVaultImage(vault: string, url: string): void {
    this._incidentsAPI
      .sendRequestImage(vault, { fileUrl: url })
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public viewDetectionDetails(item: any, data: any, queryParams: any): void {
    this._dialog
      .open(DetectionDetailsDialogComponent, {
        data: {
          detectionsImageUrl: item.detectionsImageUrl,
          detection: item,
          detectionFiltered: data,
          queryParams,
        },
        panelClass: 'detection-details__dialog-container',
      })
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe();
  }

  public selectChannel(url: any): void {
    const imageUrl = url.detectionsImageUrl || url;
    const dialogRef = this._dialog.open(ChannelsToShareDialogComponent);
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe((channel) => {
        if (channel?.twilioChannel) {
          this._send(channel, imageUrl);
        }
      });
  }

  public incorrectSentryResult(detectionItem: any, cameraDetections: any, queryParams: any): void {
    this.reporting = true;
    this._camerasAPI
      .flagAiDetection(detectionItem.id, cameraDetections.detectionId, queryParams)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          this._toastrService.info(t('map.detectionDetails.reportReceived'), '', {
            positionClass: 'toast-top-right',
          });
        },
        error: () => {
          this._toastrService.error(t('map.detectionDetails.detectionAlreadyReported'), '', {
            positionClass: 'toast-top-right',
          });
        },
        complete: () => {
          this.reporting = false;
          this.reported = true;
        },
      });
  }

  public clip(url: any): void {
    const dialog = this._dialog.open(SnShareImageDialogComponent, {
      data: { url },
    });
    dialog
      .afterClosed()
      .pipe(takeUntil(this._destroy$))
      .subscribe(() => {});
  }

  private _send(channel: SnChannel, url: string): void {
    const imageUrl = url;
    fetch(`${imageUrl}`).then(async (response) => {
      const blob = await response.blob();
      const now = new Date();
      const name = `${now.getTime()}`;
      const file: any = blob;
      file.lastModifiedDate = new Date();
      file.name = `image-${name}.jpeg`;
      this._messages
        .sendMediaMessage(channel, file)
        .pipe(takeUntil(this._destroy$))
        .subscribe({
          error: () => {
            this._toastrService.error(t('common.failedToSend'));
          },
        });
    });
  }
}
